import { useApolloClient, useLazyQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import PrimaryButton from "@components/buttons/PrimaryButton";
import SecondaryButton from "@components/buttons/SecondaryButton";
import TertiaryButton from "@components/buttons/TertiaryButton";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import DateStamp from "@components/dates/DateStamp";
import styles from "@components/expert/ExpertCarePlansPage.module.css";
import ExpertRoleBoxes from "@components/ExpertRoleBoxes";
import CarePlansFilters from "@components/forms/CarePlansFilters";
import HandleQuery from "@components/HandleQuery";
import Nbsp from "@components/Nbsp";
import ExpertTablesPage from "@components/pageTypes/ExpertTablesPage";
import Table, { TableHeader } from "@components/Table";
import HugeText from "@components/text/HugeText";
import Icon from "@components/text/Icon";
import Link from "@components/text/Link";
import useSubscribeToJourneys from "@hooks/subscriptions/useSubscribeToJourneys";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import {
  CarePlanSortByEnum,
  CarePlanStatusEnum,
  ClientAccessTypeEnum,
  ConsultReportStatusEnum,
  GetExpertCarePlansDocument,
  GetExpertCarePlansQuery,
  GetExpertCarePlansQueryVariables,
  SortDirectionEnum
} from "@typing/Generated";
import { getCareProfileName } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Filters = ComponentProps<typeof CarePlansFilters>["filters"];

const ExpertCarePlansPage = () => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const apolloClient = useApolloClient();

  const [sortBy, setSortBy] = useState<CarePlanSortByEnum>(CarePlanSortByEnum.status);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.ascending);

  const [filters, setFilters] = useState<Filters>({
    expertArchived: false,
    expertId: null,
    icrStatus: ConsultReportStatusEnum.all,
    status: null
  });

  const [getCarePlans, getCarePlansQuery] = useLazyQuery<GetExpertCarePlansQuery, GetExpertCarePlansQueryVariables>(
    GetExpertCarePlansDocument
  );

  useSubscribeToJourneys(() => {
    // This kicks out all of the expertCarePlans queries so we will re-make them. If you're flipping between filters
    // then the queries related to them will be cached, but if a journey is changed then we need to redo those queries.
    apolloClient.cache.evict({ fieldName: "expertCarePlans", id: "ROOT_QUERY" });

    getCarePlansQuery.refetch();
  });

  useEffect(() => {
    if (myExpert) {
      getCarePlans({
        variables: {
          expertArchived: filters.expertArchived,
          expertId: filters.expertId ?? null,
          icrStatus: filters.icrStatus,
          sortBy,
          sortDirection,
          status: filters.status
        }
      });
    }
  }, [filters, getCarePlans, intl, myExpert, sortBy, sortDirection]);

  const journeys = useNodesFromConnection(getCarePlansQuery.data?.expertCarePlans);

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const tableHeaders: TableHeader<CarePlanSortByEnum>[] = [
    { columnSortBy: CarePlanSortByEnum.client_name },
    { columnSortBy: CarePlanSortByEnum.loved_one_name },
    { columnSortBy: CarePlanSortByEnum.employer_name },
    { columnSortBy: CarePlanSortByEnum.primary_expert },
    { columnSortBy: CarePlanSortByEnum.status },
    { columnSortBy: CarePlanSortByEnum.care_plan_last_updated },
    { columnSortBy: CarePlanSortByEnum.care_plan_due_date },
    { columnSortBy: CarePlanSortByEnum.care_plan }
  ];

  return (
    <ExpertTablesPage helmetKey="pageTitles.expert.carePlan.list">
      <div className={styles.goBack}>
        <TertiaryButton
          icon="chevronLeft"
          routerLink={`/expert/dashboard?expertId=${filters.expertId ?? ""}`}
          textKey="pages.expert.consults.dashboard"
        />
      </div>
      <div className={styles.title}>
        <HugeText>
          <FormattedMessage id="pages.expert.journeys.carePlans" />
        </HugeText>
      </div>
      <div className={styles.tableContainer}>
        <CarePlansFilters filters={filters} onChange={handleFilterChange} />
      </div>
      <HandleQuery query={getCarePlansQuery}>
        {_data => (
          <>
            <div className={styles.tableContainer}>
              <div className={styles.carePlansCount}>
                <FormattedMessage
                  id="pages.carePlan.carePlanCount"
                  values={{ count: getCarePlansQuery.data?.expertCarePlans.totalCount }}
                />
              </div>
            </div>
            <Table<CarePlanSortByEnum>
              fetchMore={getCarePlansQuery.fetchMore}
              headerKey="pages.expert.carePlans.tableHeadings"
              headers={tableHeaders}
              pageInfo={getCarePlansQuery.data?.expertCarePlans.pageInfo}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              sortBy={sortBy}
              sortDirection={sortDirection}
            >
              {journeys.map(journey => (
                <tr key={journey.id}>
                  <td>
                    <div className={styles.bigText}>
                      {journey.owner.lastName}
                      <FormattedMessage id="dictionary.punctuation.comma" />
                      <Nbsp />
                      {journey.owner.firstName}
                    </div>
                  </td>
                  <td>
                    <Link
                      className={styles.bigText}
                      data-test="journey-route"
                      to={journeyRoute({ journeyId: journey.id, linkAfterJourney: "", myExpert })}
                    >
                      {getCareProfileName({ intl, journey })}
                    </Link>
                  </td>
                  <td>
                    {journey.owner.accessType === ClientAccessTypeEnum.EMPLOYER_SPONSORED ? (
                      <>{journey.owner.employer?.name}</>
                    ) : (
                      <FormattedMessage id="dictionary.selfPaid" />
                    )}
                  </td>
                  <td className={styles.lessPadding}>
                    <ExpertRoleBoxes expertRoles={journey.expertRoles} />
                  </td>
                  <td>
                    <div className={styles.noWrap}>
                      {journey.carePlanStatus === CarePlanStatusEnum.OVERDUE && (
                        <Icon color="danger" icon="alarmClock" spacingStart />
                      )}
                      <FormattedMessage id={`models.carePlan.status.${journey.carePlanStatus}`} />
                    </div>
                  </td>
                  <td>
                    {journey.carePlanUpdatedAt && journey.carePlanPublishedAt && (
                      <DateStamp showWeekday={false} value={journey.carePlanUpdatedAt} />
                    )}
                  </td>
                  <td>
                    {journey.carePlanDueDate && (
                      <DateAndTimeStamp showWeekday={false} value={journey.carePlanDueDate} />
                    )}
                  </td>
                  <td>
                    {journey.carePlanPublishedAt ? (
                      <SecondaryButton
                        expand="block"
                        routerLink={journeyRoute({ journeyId: journey.id, linkAfterJourney: "/care-plan", myExpert })}
                        textKey="pages.expert.carePlans.viewPublished"
                      />
                    ) : (
                      <PrimaryButton
                        expand="block"
                        routerLink={journeyRoute({ journeyId: journey.id, linkAfterJourney: "/care-plan", myExpert })}
                        textKey="pages.expert.carePlans.viewCarePlan"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </HandleQuery>
    </ExpertTablesPage>
  );
};

export default ExpertCarePlansPage;
